import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import { fmtDate, fmtDays, YEAR_OPTIONS } from "@/pages/hr/leave/leaveUtils";

export default function LeaveHistoricalTab({ employees = [], types = [] }) {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ employee_id: "", year: "", leave_type_id: "" });

  const load = () => {
    const q = Object.entries(f).filter(([, v]) => v).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
    api.get(`/hr/leave/historical${q ? "?" + q : ""}`).then((r) => setRows(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [f]);

  return (
    <div data-testid="leave-historical-tab">
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-2.5 mb-3 text-xs text-amber-800 flex items-center gap-2" data-testid="historical-note">
        <Lock className="w-3.5 h-3.5" />Imported AutoCount leave history — read-only, for reference. These do not affect ERP opening balances.
      </div>
      <div className="flex flex-wrap gap-2 mb-3 items-end rounded-lg border border-slate-200 bg-white p-3">
        <div><Label className="text-xs">Employee</Label>
          <Select value={f.employee_id || "all"} onValueChange={(v) => setF({ ...f, employee_id: v === "all" ? "" : v })}>
            <SelectTrigger className="h-8 w-48" data-testid="hist-filter-employee"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All employees</SelectItem>{employees.map((e) => <SelectItem key={e.employee_id} value={e.employee_id}>{e.employee_id} · {e.full_name}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label className="text-xs">Year</Label>
          <Select value={f.year || "all"} onValueChange={(v) => setF({ ...f, year: v === "all" ? "" : v })}>
            <SelectTrigger className="h-8 w-28" data-testid="hist-filter-year"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All years</SelectItem>{YEAR_OPTIONS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label className="text-xs">Type</Label>
          <Select value={f.leave_type_id || "all"} onValueChange={(v) => setF({ ...f, leave_type_id: v === "all" ? "" : v })}>
            <SelectTrigger className="h-8 w-36" data-testid="hist-filter-type"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All types</SelectItem>{types.map((t) => <SelectItem key={t.id} value={t.id}>{t.code}</SelectItem>)}</SelectContent>
          </Select></div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
        <table className="excel-table w-full" data-testid="leave-historical-table">
          <thead><tr><th>Employee</th><th>Type</th><th>Apply Date</th><th>Dates</th><th>Days</th><th>Status</th><th>Reason</th><th></th></tr></thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={8} className="text-center text-slate-400 py-6">No historical records</td></tr>
              : rows.map((r) => (
                <tr key={r.id} data-testid={`hist-row-${r.external_trans_id}`}>
                  <td className="text-xs"><span className="font-mono">{r.employee_id || r.autocount_employee_code}</span></td>
                  <td className="text-xs">{r.leave_type_code}</td>
                  <td className="text-xs font-mono">{fmtDate(r.apply_date)}</td>
                  <td className="text-xs">{(r.leave_dates || []).slice(0, 2).map((d) => fmtDate(d)).join(", ")}{(r.leave_dates || []).length > 2 ? ` +${r.leave_dates.length - 2}` : ""}</td>
                  <td className="text-center">{fmtDays(r.day_no)}</td>
                  <td className="text-xs">{r.status}</td>
                  <td className="text-xs max-w-[180px] truncate" title={r.apply_reason}>{r.apply_reason || "—"}</td>
                  <td><Badge variant="outline" className="text-amber-700 border-amber-300 text-[10px]">AutoCount Historical</Badge></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
